/**
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at

 * http://www.apache.org/licenses/LICENSE-2.0

 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { isNumber, isObject } from './typeChecks'
import { Decimal } from 'decimal.js'
// import { formatSwapPrice } from '~/utils/utils.js'

/**
 * 格式化值
 * @param data
 * @param key
 * @param defaultValue
 * @returns {string|*}
 */
export function formatValue(data, key, defaultValue = '--') {
    if (data && isObject(data)) {
        const value = data[key]
        if (value || value === 0 || value === false) {
            return value
        }
    }
    return defaultValue
}

/**
 * 格式化时间
 * @param dateTimeFormat
 * @param timestamp
 * @param format
 * @returns {string}
 */
export function formatDate(dateTimeFormat, timestamp, format = 'MM-DD hh:mm') {
    if (timestamp && isNumber(timestamp)) {
        const dateTimeString = dateTimeFormat.format(new Date(timestamp))
        const dateTimeStringArray = dateTimeString.split(', ')
        const dateStringArray = dateTimeStringArray[0].split('/')
        const date = {
            YYYY: dateStringArray[2],
            MM: dateStringArray[0],
            DD: dateStringArray[1],
            'hh:mm':
                dateTimeStringArray[1].match(/^[\d]{2}/)[0] === '24'
                    ? dateTimeStringArray[1].replace(/^[\d]{2}/, '00')
                    : dateTimeStringArray[1]
        }
        return format.replace(/YYYY|MM|DD|(hh:mm)/g, (key) => date[key])
    }
    return '--'
}
function formatSwapPrice(v) {
    let price = String(v)
    if (price.indexOf('e') !== -1) {
        price = toNonExponential(+price)
    }
    if (isNaN(price)) return ''
    if (price == 0) return '0.00'
    // 价格大于等于1U的代币，四舍五入后，显示小数点后2位
    if (price >= 1) {
        return Number(price).toFixed(2)
    } else {
        // 小数点后面是否以0开头,不为0，直接显示4位
        let index = price.indexOf('.')
        if (price[index + 1] != 0) {
            return Number(price).toFixed(4)
        }
        // 计算小数点后连续0的个数
        // 0.01234      -> 1
        // 0.00001234   -> 4
        let zeroLength = 0
        // 保留小数点前面的值
        let preNum = price.split('.')[0]
        // 截取小数点后的值，并且转成array，计算zeroLength的长度
        let zeroPriceArr = price.substring(index + 1).split('')
        for (let i = 0; i < zeroPriceArr.length; i++) {
            if (zeroPriceArr[i] == 0) {
                zeroLength += 1
            } else {
                break
            }
        }
        let fixedNum = zeroLength + 4
        // 四舍五入后的值
        let value = Number(price).toFixed(fixedNum)
        let zeroIdx = value.indexOf('.')
        let nextNum = value.substring(zeroIdx + 1 + zeroLength)
        // 求出转换后是否有进制的情况存在
        // 示例
        // 0.00009999 -> 0.00010000
        let newZeroLength = 0
        // 保留小数点前面的值
        let newPreNum = value.split('.')[0]
        // 截取小数点后的值，并且转成array，计算zeroLength的长度
        let newZeroPriceArr = value.substring(index + 1).split('')
        for (let i = 0; i < newZeroPriceArr.length; i++) {
            if (newZeroPriceArr[i] == 0) {
                newZeroLength += 1
            } else {
                break
            }
        }
        // 示例
        // 0.00001234 -> 0.0{4}1234
        // 0.0001234 -> 0.0001234
        if (zeroLength >= 4 && newZeroLength == zeroLength) {
            return `${preNum}.{${zeroLength}}${nextNum}`
        } else {
            return value
        }
        // 后置除了连续0的数字
        // return Number(price).toFixed(fixedNum)
    }
}
/**
 * 格式化精度
 */
export function formatPrecision(value, precision = 2) {
    const v = +value
    // return klinetoFixed(v)
    if ((v || v === 0) && isNumber(v)) {
        return formatSwapPrice(v)
        // return v.toFixed(precision)
    }
    return `${v}`
}

// 将科学计数法转换为小数
function toNonExponential(num) {
    var m = num.toExponential().match(/\d(?:\.(\d*))?e([+-]\d+)/)
    return num.toFixed(Math.max(0, (m[1] || '').length - m[2]))
}

function klinetoFixed(number) {
    let num = new Decimal(number)
    num = num.toString()
    if (num.indexOf('e') !== -1) {
        num = toNonExponential(+num)
    }
    if (+num > 10) {
        return Number(num).toFixed(2)
    } else if (+num >= 1) {
        return Number(num).toFixed(3)
    } else {
        let arr = num.split('.')
        if (arr.length !== 2) return num
        let index = arr[1].split('').findIndex((i) => {
            return i != 0
        })
        if (index === arr[1].split('').length - 1 && arr[1].split('')[index] == 1) {
            index = index - 1
        }
        return Number(num).toFixed(index + 4)
    }
}

/**
 * 格式化大数据
 * @param value
 */
export function formatBigNumber(value) {
    if (isNumber(+value)) {
        if (value > 1000000000) {
            return `${+(value / 1000000000).toFixed(3)}B`
        }
        if (value > 1000000) {
            return `${+(value / 1000000).toFixed(3)}M`
        }
        if (value > 1000) {
            return `${+(value / 1000).toFixed(3)}K`
        }
        return value
    }
    return '--'
}
